import React, { useState, useEffect, useMemo } from 'react';
import { Search, Building2, Users, X, ChevronDown, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { usePlanningStore } from '@/stores/planningStore';

// Pictogrammes disponibles pour les chantiers
const CHANTIER_PICTOGRAMS: Record<string, string> = {
  building: '🏢',
  house: '🏠',
  factory: '🏭',
  office: '🏛️',
  hospital: '🏥',
  school: '🏫',
  hotel: '🏨',
  store: '🏪',
  warehouse: '🏬',
  default: '🏗️',
};

// Pictogrammes disponibles pour les équipes
const EQUIPE_PICTOGRAMS: Record<string, string> = {
  electricite: '⚡',
  plomberie: '🔧',
  maconnerie: '🧱',
  menuiserie: '🪚',
  peinture: '🎨',
  carrelage: '🔲',
  placo: '📐',
  toiture: '🏠',
  default: '👷',
};

interface Chantier {
  id: string;
  nom_chantier: string;
  client?: string;
  etat_chantier?: string;
  pictogramme?: string;
  couleur?: string;
}

interface Equipe {
  id: string;
  nom: string;
  specialite?: string;
  pictogramme?: string;
  couleur?: string;
}

interface Membre {
  id: string;
  nom: string;
  prenom: string;
  equipe_id?: string;
}

interface PlanningFiltersProps {
  chantiers: Chantier[];
  equipes: Equipe[];
  membres: Membre[];
}

/**
 * Barre de filtres pour le planning
 * - Recherche texte (debounce 300ms)
 * - Combobox Chantier (multi-sélection, groupé par statut)
 * - Combobox Équipe (multi-sélection, avec nombre de membres)
 * - Badges des filtres actifs
 */
export function PlanningFilters({ chantiers, equipes, membres }: PlanningFiltersProps) {
  const {
    filters,
    setSearchQuery,
    toggleChantierFilter,
    toggleEquipeFilter,
    clearFilters,
    removeChantierFilter,
    removeEquipeFilter,
  } = usePlanningStore();

  const [searchInput, setSearchInput] = useState(filters.searchQuery);
  const [chantierOpen, setChantierOpen] = useState(false);
  const [equipeOpen, setEquipeOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [cmdkSearch, setCmdkSearch] = useState('');

  const isSearchActive = cmdkSearch.length > 0;

  const toggleGroupOpen = (groupKey: string) => {
    setOpenGroup(prev => prev === groupKey ? null : groupKey);
  };

  // Debounce la recherche (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setSearchQuery]);

  // Sync l'input si le filtre est effacé depuis ailleurs
  useEffect(() => {
    if (filters.searchQuery === '' && searchInput !== '') {
      setSearchInput('');
    }
  }, [filters.searchQuery]);

  // Grouper les chantiers par statut (En cours d'abord, puis Planifié, puis Autres)
  const groupedChantiers = useMemo(() => {
    const enCours = chantiers.filter(c => c.etat_chantier === 'en_cours');
    const planifie = chantiers.filter(c => c.etat_chantier === 'planifie');
    const autres = chantiers.filter(c =>
      c.etat_chantier === 'suspendu' ||
      c.etat_chantier === 'termine' ||
      c.etat_chantier === 'archive'
    );
    return { enCours, planifie, autres };
  }, [chantiers]);

  // Filtrer les membres selon la recherche (pour autocomplete)
  const filteredMembres = useMemo(() => {
    if (searchInput.length < 2) return [];
    const query = searchInput.toLowerCase().trim();
    return membres.filter(m => {
      const nom = m.nom?.toLowerCase() || '';
      const prenom = m.prenom?.toLowerCase() || '';
      return nom.startsWith(query) || prenom.startsWith(query) ||
             nom.includes(query) || prenom.includes(query);
    }).slice(0, 3); // Limiter à 3 suggestions
  }, [membres, searchInput]);

  // Filtrer les chantiers selon la recherche (pour autocomplete)
  const filteredChantiersSearch = useMemo(() => {
    if (searchInput.length < 2) return [];
    const query = searchInput.toLowerCase().trim();
    return chantiers.filter(c => {
      const nom = c.nom_chantier?.toLowerCase() || '';
      return nom.includes(query);
    }).slice(0, 3); // Limiter à 3 suggestions
  }, [chantiers, searchInput]);

  // Compter les membres par équipe
  const membresParEquipe = useMemo(() => {
    const counts: Record<string, number> = {};
    membres.forEach(m => {
      if (m.equipe_id) {
        counts[m.equipe_id] = (counts[m.equipe_id] || 0) + 1;
      }
    });
    return counts;
  }, [membres]);

  // Nombre total de filtres actifs
  const activeFiltersCount = filters.chantierIds.length + filters.equipeIds.length;

  // Récupérer les noms des chantiers et équipes sélectionnés pour les badges
  const selectedChantiers = useMemo(() =>
    chantiers.filter(c => filters.chantierIds.includes(c.id)),
    [chantiers, filters.chantierIds]
  );

  const selectedEquipes = useMemo(() =>
    equipes.filter(e => filters.equipeIds.includes(e.id)),
    [equipes, filters.equipeIds]
  );

  const getPictogram = (picto: string | undefined, defaults: Record<string, string>) => {
    if (!picto) return defaults.default;
    return defaults[picto] || defaults.default;
  };

  return (
    <div className="space-y-3">
      {/* Barre de filtres principale */}
      <div className="flex items-center gap-3">
        {/* Recherche texte avec suggestions - toujours visible */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
          <Input
            placeholder="Rechercher chantier, membre..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded z-10"
            >
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
          {/* Suggestions de recherche (chantiers + membres) */}
          {(filteredChantiersSearch.length > 0 || filteredMembres.length > 0) && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-[80] overflow-hidden max-h-64 overflow-y-auto">
              {/* Suggestions chantiers */}
              {filteredChantiersSearch.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs text-muted-foreground border-b bg-muted/30">
                    <Building2 className="w-3 h-3 inline mr-1" />
                    Chantiers
                  </div>
                  {filteredChantiersSearch.map((chantier) => (
                    <button
                      key={chantier.id}
                      onClick={() => setSearchInput(chantier.nom_chantier)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                    >
                      <span
                        className="w-5 h-5 rounded flex items-center justify-center text-xs"
                        style={{ backgroundColor: chantier.couleur || '#3B82F6' }}
                      >
                        {getPictogram(chantier.pictogramme, CHANTIER_PICTOGRAMS)}
                      </span>
                      <span className="flex-1 truncate">{chantier.nom_chantier}</span>
                    </button>
                  ))}
                </>
              )}
              {/* Suggestions membres */}
              {filteredMembres.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs text-muted-foreground border-b bg-muted/30">
                    <Users className="w-3 h-3 inline mr-1" />
                    Membres
                  </div>
                  {filteredMembres.map((membre) => (
                    <button
                      key={membre.id}
                      onClick={() => setSearchInput(`${membre.prenom} ${membre.nom}`)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
                    >
                      <div className="w-5 h-5 rounded-full bg-sky-400/20 flex items-center justify-center text-xs font-medium text-sky-400">
                        {membre.prenom?.[0]}{membre.nom?.[0]}
                      </div>
                      <span>{membre.prenom} {membre.nom}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Combobox Chantier */}
        <Popover open={chantierOpen} onOpenChange={(open) => {
          setChantierOpen(open);
          if (!open) {
            setOpenGroup(null);
            setCmdkSearch('');
          }
        }}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-9 gap-2",
                filters.chantierIds.length > 0 && "border-primary bg-primary/5"
              )}
            >
              <Building2 className="w-4 h-4" />
              Chantiers
              {filters.chantierIds.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-primary text-primary-foreground">
                  {filters.chantierIds.length}
                </Badge>
              )}
              <ChevronDown className="w-3 h-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0 z-[70]" align="start">
            <Command>
              <CommandInput placeholder="Rechercher un chantier..." onValueChange={(value) => setCmdkSearch(value)} />
              <CommandList>
                <CommandEmpty>Aucun chantier trouvé</CommandEmpty>

                {/* Groupe "En cours" - accordéon */}
                {groupedChantiers.enCours.length > 0 && (
                  <CommandGroup
                    forceMount
                    heading={
                      isSearchActive ? (
                        `En cours (${groupedChantiers.enCours.length})`
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleGroupOpen('enCours'); }}
                          className="flex w-full items-center justify-between cursor-pointer hover:text-foreground transition-colors"
                        >
                          <span>En cours ({groupedChantiers.enCours.length})</span>
                          <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", openGroup !== 'enCours' && "-rotate-90")} />
                        </button>
                      )
                    }
                  >
                    {(isSearchActive || openGroup === 'enCours') &&
                      groupedChantiers.enCours.map((chantier) => (
                        <CommandItem
                          key={chantier.id}
                          value={chantier.nom_chantier}
                          onSelect={() => toggleChantierFilter(chantier.id)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Checkbox
                            checked={filters.chantierIds.includes(chantier.id)}
                            className="pointer-events-none"
                          />
                          <span
                            className="w-5 h-5 rounded flex items-center justify-center text-sm"
                            style={{ backgroundColor: chantier.couleur || '#3B82F6' }}
                          >
                            {getPictogram(chantier.pictogramme, CHANTIER_PICTOGRAMS)}
                          </span>
                          <span className="flex-1 truncate">{chantier.nom_chantier}</span>
                          {chantier.client && (
                            <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                              {chantier.client}
                            </span>
                          )}
                        </CommandItem>
                      ))
                    }
                  </CommandGroup>
                )}

                {/* Groupe "Planifié" - accordéon */}
                {groupedChantiers.planifie.length > 0 && (
                  <CommandGroup
                    forceMount
                    heading={
                      isSearchActive ? (
                        `Planifié (${groupedChantiers.planifie.length})`
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleGroupOpen('planifie'); }}
                          className="flex w-full items-center justify-between cursor-pointer hover:text-foreground transition-colors"
                        >
                          <span>Planifié ({groupedChantiers.planifie.length})</span>
                          <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", openGroup !== 'planifie' && "-rotate-90")} />
                        </button>
                      )
                    }
                  >
                    {(isSearchActive || openGroup === 'planifie') &&
                      groupedChantiers.planifie.map((chantier) => (
                        <CommandItem
                          key={chantier.id}
                          value={chantier.nom_chantier}
                          onSelect={() => toggleChantierFilter(chantier.id)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Checkbox
                            checked={filters.chantierIds.includes(chantier.id)}
                            className="pointer-events-none"
                          />
                          <span
                            className="w-5 h-5 rounded flex items-center justify-center text-sm"
                            style={{ backgroundColor: chantier.couleur || '#3B82F6' }}
                          >
                            {getPictogram(chantier.pictogramme, CHANTIER_PICTOGRAMS)}
                          </span>
                          <span className="flex-1 truncate">{chantier.nom_chantier}</span>
                          {chantier.client && (
                            <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                              {chantier.client}
                            </span>
                          )}
                        </CommandItem>
                      ))
                    }
                  </CommandGroup>
                )}

                {/* Groupe "Archivés / Terminés" - accordéon */}
                {groupedChantiers.autres.length > 0 && (
                  <CommandGroup
                    forceMount
                    heading={
                      isSearchActive ? (
                        `Archivés / Terminés (${groupedChantiers.autres.length})`
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleGroupOpen('autres'); }}
                          className="flex w-full items-center justify-between cursor-pointer hover:text-foreground transition-colors"
                        >
                          <span>Archivés / Terminés ({groupedChantiers.autres.length})</span>
                          <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", openGroup !== 'autres' && "-rotate-90")} />
                        </button>
                      )
                    }
                  >
                    {(isSearchActive || openGroup === 'autres') &&
                      groupedChantiers.autres.map((chantier) => (
                        <CommandItem
                          key={chantier.id}
                          value={chantier.nom_chantier}
                          onSelect={() => toggleChantierFilter(chantier.id)}
                          className="flex items-center gap-2 cursor-pointer opacity-70"
                        >
                          <Checkbox
                            checked={filters.chantierIds.includes(chantier.id)}
                            className="pointer-events-none"
                          />
                          <span
                            className="w-5 h-5 rounded flex items-center justify-center text-sm"
                            style={{ backgroundColor: chantier.couleur || '#3B82F6' }}
                          >
                            {getPictogram(chantier.pictogramme, CHANTIER_PICTOGRAMS)}
                          </span>
                          <span className="flex-1 truncate">{chantier.nom_chantier}</span>
                          <span className="text-xs text-muted-foreground">
                            {chantier.etat_chantier === 'archive' ? '📦' : chantier.etat_chantier === 'termine' ? '✅' : '⏸️'}
                          </span>
                        </CommandItem>
                      ))
                    }
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Combobox Équipe */}
        <Popover open={equipeOpen} onOpenChange={setEquipeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-9 gap-2",
                filters.equipeIds.length > 0 && "border-primary bg-primary/5"
              )}
            >
              <Users className="w-4 h-4" />
              Équipes
              {filters.equipeIds.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-primary text-primary-foreground">
                  {filters.equipeIds.length}
                </Badge>
              )}
              <ChevronDown className="w-3 h-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 z-[70]" align="start">
            <Command>
              <CommandInput placeholder="Rechercher une équipe..." />
              <CommandList>
                <CommandEmpty>Aucune équipe trouvée</CommandEmpty>
                <CommandGroup>
                  {equipes.map((equipe) => (
                    <CommandItem
                      key={equipe.id}
                      value={equipe.nom}
                      onSelect={() => toggleEquipeFilter(equipe.id)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={filters.equipeIds.includes(equipe.id)}
                        className="pointer-events-none"
                      />
                      <span
                        className="w-5 h-5 rounded flex items-center justify-center text-sm"
                        style={{ backgroundColor: equipe.couleur || '#10B981' }}
                      >
                        {getPictogram(equipe.pictogramme, EQUIPE_PICTOGRAMS)}
                      </span>
                      <span className="flex-1 truncate">{equipe.nom}</span>
                      <span className="text-xs text-muted-foreground">
                        ({membresParEquipe[equipe.id] || 0})
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Badges des filtres actifs */}
      {(activeFiltersCount > 0 || filters.searchQuery) && (
        <div className="flex items-center gap-2 flex-wrap">
          {/* Badge recherche */}
          {filters.searchQuery && (
            <Badge variant="secondary" className="h-6 gap-1 pr-1">
              <Search className="w-3 h-3" />
              "{filters.searchQuery}"
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                }}
                className="ml-1 p-0.5 hover:bg-muted rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}

          {/* Badges chantiers */}
          {selectedChantiers.map((chantier) => (
            <Badge
              key={chantier.id}
              variant="secondary"
              className="h-6 gap-1 pr-1"
              style={{ borderColor: chantier.couleur || '#3B82F6' }}
            >
              <span
                className="w-4 h-4 rounded flex items-center justify-center text-xs"
                style={{ backgroundColor: chantier.couleur || '#3B82F6' }}
              >
                {getPictogram(chantier.pictogramme, CHANTIER_PICTOGRAMS)}
              </span>
              <span className="max-w-[120px] truncate">{chantier.nom_chantier}</span>
              <button
                onClick={() => removeChantierFilter(chantier.id)}
                className="ml-1 p-0.5 hover:bg-muted rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}

          {/* Badges équipes */}
          {selectedEquipes.map((equipe) => (
            <Badge
              key={equipe.id}
              variant="secondary"
              className="h-6 gap-1 pr-1"
              style={{ borderColor: equipe.couleur || '#10B981' }}
            >
              <span
                className="w-4 h-4 rounded flex items-center justify-center text-xs"
                style={{ backgroundColor: equipe.couleur || '#10B981' }}
              >
                {getPictogram(equipe.pictogramme, EQUIPE_PICTOGRAMS)}
              </span>
              <span className="max-w-[100px] truncate">{equipe.nom}</span>
              <button
                onClick={() => removeEquipeFilter(equipe.id)}
                className="ml-1 p-0.5 hover:bg-muted rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}

          {/* Bouton Effacer tout */}
          {(activeFiltersCount > 0 || filters.searchQuery) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Effacer tout
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
